import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_swiss-logistics/artifacts/6ybdvhfm_BUS.jpg";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="accueil" ref={ref} className="hero-parallax relative min-h-screen flex items-center" data-testid="hero-section">
      {/* Parallax Background */}
      <motion.div
        className="hero-bg"
        style={{
          y,
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          scale: 1.1,
        }}
      />
      <div className="hero-overlay" />

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-20"
      >
        <div className="max-w-3xl">
          {/* Red accent line */}
          <motion.div
            initial={{ width: 0 }}
            animate={loaded ? { width: 80 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-1 bg-[#FF0000] mb-8"
          />

          {/* Main title */}
          <motion.h1
            initial={{ opacity: 0, y: 60 }}
            animate={loaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-['Oswald'] text-4xl sm:text-5xl lg:text-7xl font-bold uppercase leading-[1.05] tracking-tight text-white mb-6"
            data-testid="hero-title"
          >
            Votre partenaire transport dans toute la Suisse
          </motion.h1>

          {/* Slogan */}
          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={loaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-base md:text-lg text-white/70 mb-10 max-w-xl leading-relaxed font-light"
            data-testid="hero-slogan"
          >
            Fiabilité, rapidité, proximité — vos biens transportés avec le soin qu'ils méritent, partout en Suisse.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={loaded ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#devis"
              onClick={(e) => scrollTo(e, '#devis')}
              className="bg-[#FF0000] hover:bg-[#D90000] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest transition-all hover:scale-105 text-center"
              data-testid="hero-cta-devis"
            >
              Demander un devis
            </a>
            <a
              href="#services"
              onClick={(e) => scrollTo(e, '#services')}
              className="border border-white/20 hover:bg-white/10 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest transition-all text-center"
              data-testid="hero-cta-services"
            >
              Nos services
            </a>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.a
          href="#services"
          onClick={(e) => scrollTo(e, '#services')}
          initial={{ opacity: 0 }}
          animate={loaded ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1.2 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer group"
          data-testid="scroll-down-indicator"
        >
          <span className="text-white/40 text-xs uppercase tracking-[0.2em] group-hover:text-white/70 transition-colors">Scroll down</span>
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2 group-hover:border-white/60 transition-colors"
          >
            <div className="w-1.5 h-1.5 bg-white rounded-full" />
          </motion.div>
        </motion.a>
      </motion.div>
    </section>
  );
}
